import { RightContent } from '../styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalEditor from '@/components/ModalEditor';
import SelectAp from './SelectAp';
import {
  radio50FieldList,
  radio24FieldList,
  wifiFields,
} from '@/utils/constants';
import { enqueueSnackbar } from 'notistack';
import api from '@/services/api';
import RadioCard from './RadioCard';
import AccessPoint from './AccessPoint';
import { useEquipmentContext } from '@/context/EquipmentContext';
import { getWifiSchema, getRadiosSchema } from '@/validations/networkSchema';
import StatsData from '@/components/ModalEditor/StatsData';
import Card from '@/components/Card';
import Typography from '@mui/material/Typography';
function Wireless({ modeView }: { modeView: string }) {
  const { t } = useTranslation();
  const { equipment, refetch, loading } = useEquipmentContext();
  const [currentAp24, setCurrentAp24] = useState(0);
  const [currentAp50, setCurrentAp50] = useState(0);

  const { wifis, radio24, radio50 } = useMemo(() => {
    const grouped: WifiListType = { '2.4GHz': [], '5GHz': [] };
    let r24: NetworkInterfaceType | undefined;
    let r50: NetworkInterfaceType | undefined;

    if (Array.isArray(equipment?.wifis)) {
      equipment.wifis.forEach((wifi: NetworkInterfaceType) => {
        const freq = wifi?.frequency?.value;
        if (freq === '2.4GHz') grouped['2.4GHz'].push(wifi);
        else if (freq === '5GHz') grouped['5GHz'].push(wifi);
      });
    }

    if (Array.isArray(equipment?.radios)) {
      r24 = equipment.radios.find(
        (r: NetworkInterfaceType) => r?.frequency?.value === '2.4GHz'
      );
      r50 = equipment.radios.find(
        (r: NetworkInterfaceType) => r?.frequency?.value === '5GHz'
      );
    }

    return { wifis: grouped, radio24: r24, radio50: r50 };
  }, [equipment]);

  /** Builds groupedOptions for SelectAp from an already-sorted wifi list */
  const buildApGroupedOptions = useCallback(
    (list: NetworkInterfaceType[]) => {
      const active: { index: number; name: string }[] = [];
      const inactive: { index: number; name: string }[] = [];
      const unknown: { index: number; name: string }[] = [];

      list.forEach((wifi, index) => {
        const ssidEnable = wifi?.ssidEnable;
        const visible = wifi?.visible;
        const name = String(wifi?.ssid?.value ?? `AP ${index + 1}`);
        const entry = { index, name };

        if (ssidEnable == null || visible == null) {
          unknown.push(entry);
        } else if (
          String(ssidEnable.value) === 'true' &&
          String(visible.value) === 'true'
        ) {
          active.push(entry);
        } else {
          inactive.push(entry);
        }
      });

      return [
        ...(active.length
          ? [{ group: t('common.active'), items: active }]
          : []),
        ...(inactive.length
          ? [{ group: t('common.inactive'), items: inactive }]
          : []),
        ...(unknown.length
          ? [{ group: t('common.unknown'), items: unknown }]
          : []),
      ];
    },
    [t]
  );

  const [currentModal, setCurrentModal] = useState<
    | 'radio24'
    | 'radio50'
    | 'wifi24'
    | 'wifi50'
    | 'apSelect24'
    | 'apSelect50'
    | null
  >(null);

  useEffect(() => {
    const list24 = wifis['2.4GHz'] || [];
    const list50 = wifis['5GHz'] || [];

    if (currentAp24 >= list24.length && list24.length > 0) setCurrentAp24(0);
    if (currentAp50 >= list50.length && list50.length > 0) setCurrentAp50(0);
  }, [wifis, currentAp24, currentAp50]);

  const handleEdit = useCallback(
    async (values: Record<string, string | number>) => {
      if (!equipment?.id) return;

      let targetInterface: NetworkInterfaceType | undefined;
      let fieldsToCheck: EditorPropType[] = [];

      switch (currentModal) {
        case 'radio24':
          targetInterface = radio24;
          fieldsToCheck = radio24FieldList;
          break;
        case 'radio50':
          targetInterface = radio50;
          fieldsToCheck = radio50FieldList;
          break;
        case 'wifi24':
          targetInterface = wifis['2.4GHz'][currentAp24];
          fieldsToCheck = wifiFields;
          break;
        case 'wifi50':
          targetInterface = wifis['5GHz'][currentAp50];
          fieldsToCheck = wifiFields;
          break;
        default:
          return;
      }

      if (!targetInterface) return;

      const changedFields = fieldsToCheck
        .map((field) => {
          const current =
            targetInterface?.[field.field as keyof NetworkInterfaceType]?.value;
          const id =
            targetInterface?.[field.field as keyof NetworkInterfaceType]?.id;
          const newValue = values[field.field] ?? '';
          if (id && String(current ?? '') !== String(newValue ?? '')) {
            return { id: id, value: newValue };
          }
          return null;
        })
        .filter(Boolean) as { id: number; value: string | number }[];

      if (changedFields.length === 0) {
        setCurrentModal(null);
        enqueueSnackbar(t('equipment.wifis.noChanges'), { variant: 'warning' });
        return;
      }

      try {
        const response = await api.patch(
          `/devices/${equipment.id}/set-parameters`,
          changedFields
        );
        if (response.status === 200) {
          refetch();
          enqueueSnackbar(t('config.models.editSuccess'), {
            variant: 'success',
          });
          setCurrentModal(null);
        }
      } catch {
        enqueueSnackbar(t('config.models.editFailed'), { variant: 'error' });
      }
    },
    [
      currentModal,
      radio24,
      radio50,
      wifis,
      currentAp24,
      currentAp50,
      t,
      equipment,
      refetch,
    ]
  );

  const modalConfig = useMemo(() => {
    if (!currentModal) return null;
    const is24 = currentModal.includes('24');
    const idx = is24 ? currentAp24 : currentAp50;
    const freqKey = is24 ? '2.4GHz' : '5GHz';

    if (currentModal.startsWith('radio')) {
      const externalOpts = (() => {
        const radio = is24 ? radio24 : radio50;
        const fieldList = is24 ? radio24FieldList : radio50FieldList;
        if (!radio) return {};

        const options: Record<string, { options: string[] }> = {};
        fieldList.forEach((field) => {
          const fieldData = radio[field.field as keyof NetworkInterfaceType];
          if (fieldData?.options && Array.isArray(fieldData.options)) {
            options[field.field] = { options: fieldData.options };
          }
        });
        return options;
      })();

      return {
        title: t(`equipment.wifis.editRadio${is24 ? '24' : '50'}Parameters`),
        icon: 'wifi' as const,
        fieldList: is24 ? radio24FieldList : radio50FieldList,
        values: (is24 ? radio24 : radio50) || {},
        handler: handleEdit,
        translatePath: 'equipment.wifis',
        schema: getRadiosSchema(t, externalOpts),
        externalOptions: externalOpts,
      };
    }

    return {
      title: `${t(`equipment.wifis.editWifi${is24 ? '24' : '50'}Parameters`)} - ${t('equipment.wifis.network')} ${idx + 1}`,
      icon: 'wifi' as const,
      fieldList: wifiFields,
      values: wifis[freqKey][idx] || {},
      handler: handleEdit,
      translatePath: 'equipment.wifis',
      externalOptions: (() => {
        const wifi = wifis[freqKey]?.[idx];
        if (!wifi) return {};

        const options: Record<string, { options: string[] }> = {};
        wifiFields.forEach((field) => {
          const fieldData = wifi[field.field as keyof NetworkInterfaceType];
          if (fieldData?.options && Array.isArray(fieldData.options)) {
            options[field.field] = { options: fieldData.options };
          }
        });
        return options;
      })(),
      schema: getWifiSchema(
        t,
        (() => {
          const wifi = wifis[freqKey]?.[idx];
          if (!wifi) return {};

          const options: Record<string, { options: string[] }> = {};
          wifiFields.forEach((field) => {
            const fieldData = wifi[field.field as keyof NetworkInterfaceType];
            if (fieldData?.options && Array.isArray(fieldData.options)) {
              options[field.field] = { options: fieldData.options };
            }
          });
          return options;
        })()
      ),
    };
  }, [
    currentModal,
    radio24,
    radio50,
    wifis,
    currentAp24,
    currentAp50,
    t,
    handleEdit,
  ]);

  return (
    <RightContent>
      <RadioCard
        radioFrequency="radio24"
        modeView={modeView}
        loading={loading}
        radio={radio24}
        setCurrentModal={setCurrentModal}
      />
      <RadioCard
        radioFrequency="radio50"
        modeView={modeView}
        loading={loading}
        radio={radio50}
        setCurrentModal={setCurrentModal}
      />
      <Card>
        <Typography variant="h3">{t(`equipment.wifis.radio24`)}</Typography>
        <StatsData fieldValues={radio24} translatePath={'equipment.radio'} />
      </Card>
      <Card>
        <Typography variant="h3">{t(`equipment.wifis.radio50`)}</Typography>
        <StatsData fieldValues={radio50} translatePath={'equipment.radio'} />
      </Card>
      <AccessPoint
        currentFrequency="2.4GHz"
        loading={loading}
        currentAp={currentAp24}
        wifis={wifis}
        modeView={modeView}
        setCurrentModal={setCurrentModal}
      />
      <AccessPoint
        currentFrequency="5GHz"
        loading={loading}
        currentAp={currentAp50}
        wifis={wifis}
        modeView={modeView}
        setCurrentModal={setCurrentModal}
      />
      {currentModal && modalConfig && !currentModal.startsWith('apSelect') && (
        <ModalEditor
          open={!!currentModal}
          setOpen={setCurrentModal}
          title={modalConfig.title}
          icon={modalConfig.icon}
          fieldList={modalConfig.fieldList}
          handleSubmit={modalConfig.handler}
          fieldValues={modalConfig.values}
          translatePath={modalConfig.translatePath}
          validationSchema={modalConfig.schema}
          externalOptions={
            modalConfig.externalOptions ? modalConfig.externalOptions : {}
          }
        />
      )}

      {/** Modal to Current 2.4GHz Access Point */}
      <SelectAp
        title={t('equipment.radio.changeAp')}
        label={t('equipment.radio.selectAp')}
        open={currentModal === 'apSelect24'}
        setOpen={setCurrentModal}
        currentValue={currentAp24}
        setCurrentValue={setCurrentAp24}
        groupedOptions={buildApGroupedOptions(wifis['2.4GHz'] ?? [])}
      />

      {/** Modal to Current 5GHz Access Point */}
      <SelectAp
        title={t('equipment.radio.changeAp')}
        label={t('equipment.radio.selectAp')}
        open={currentModal === 'apSelect50'}
        setOpen={setCurrentModal}
        currentValue={currentAp50}
        setCurrentValue={setCurrentAp50}
        groupedOptions={buildApGroupedOptions(wifis['5GHz'] ?? [])}
      />
    </RightContent>
  );
}

export default Wireless;
